import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { formatApiError } from "@/lib/utils";
import { Eye, EyeOff } from "lucide-react";

const digits = (v) => v.replace(/\D/g, "").slice(0, 6);
const when = (iso) => new Date(iso).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" });

// CS 22.4 — HO master-admin only: reveal current link PIN on demand + reset with audit log.
export const LinkPinDialog = ({ link, onClose, onChanged }) => {
  const [pin, setPin] = useState(null);
  const [resets, setResets] = useState([]);
  const [err, setErr] = useState(null);
  const [shown, setShown] = useState(false);
  const [f, setF] = useState({ pin: "", confirm_pin: "" });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!link) return;
    setPin(null); setErr(null); setShown(false); setF({ pin: "", confirm_pin: "" });
    api.get(`/master-catalog/links/${link.id}/pin`)
      .then((r) => { setPin(r.data.pin); setResets(r.data.pin_resets || []); })
      .catch((e) => setErr(formatApiError(e.response?.data?.detail) || "Could not load PIN"));
  }, [link]);

  const valid = /^\d{4,6}$/.test(f.pin) && f.pin === f.confirm_pin;
  const reset = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/master-catalog/links/${link.id}/reset-pin`, f);
      setPin(f.pin); setResets(data.pin_resets || []); setShown(false); setErr(null);
      setF({ pin: "", confirm_pin: "" });
      toast.success("PIN reset — the old PIN no longer works");
      onChanged?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Reset failed");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={!!link} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-white" data-testid="mc-link-pin-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl">Link PIN · {link?.name}</DialogTitle>
          <DialogDescription>Visible to master admin only. Resetting signs out anyone using the old PIN.</DialogDescription>
        </DialogHeader>
        <div className="rounded-lg bg-[#FAF8F3] px-4 py-3 flex items-center justify-between" data-testid="mc-link-pin-current">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#8a6d1f]">Current PIN</div>
            {err ? <div className="text-sm text-red-700" data-testid="mc-link-pin-error">{err}</div>
              : <div className="font-mono text-2xl tracking-[0.35em] text-[#0D1B2A]" data-testid="mc-link-pin-value">
                  {pin == null ? "…" : shown ? pin : "•".repeat(pin.length)}
                </div>}
          </div>
          {pin != null && (
            <Button variant="outline" size="sm" onClick={() => setShown(!shown)} data-testid="mc-link-pin-reveal">
              {shown ? <><EyeOff className="w-4 h-4 mr-1.5" />Hide</> : <><Eye className="w-4 h-4 mr-1.5" />Reveal</>}
            </Button>
          )}
        </div>
        <div className="space-y-2">
          <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500">Reset PIN</div>
          <div className="grid grid-cols-2 gap-2">
            <Input value={f.pin} onChange={(e) => setF({ ...f, pin: digits(e.target.value) })} inputMode="numeric" type="password"
              placeholder="New PIN (4–6 digits)" className="tracking-[0.3em]" data-testid="mc-link-pin-new" />
            <Input value={f.confirm_pin} onChange={(e) => setF({ ...f, confirm_pin: digits(e.target.value) })} inputMode="numeric" type="password"
              placeholder="Confirm new PIN" className="tracking-[0.3em]" data-testid="mc-link-pin-confirm" />
          </div>
          {f.confirm_pin && f.pin !== f.confirm_pin && <div className="text-xs text-red-600" data-testid="mc-link-pin-mismatch">PINs do not match</div>}
          <Button onClick={reset} disabled={!valid || busy} className="bg-[#0D1B2A] text-white" data-testid="mc-link-pin-reset-submit">
            {busy ? "Saving…" : "Reset PIN"}
          </Button>
        </div>
        <div className="border-t border-[#0D1B2A]/10 pt-3" data-testid="mc-link-pin-log">
          <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-1">Reset history</div>
          {resets.length === 0 ? <div className="text-xs text-slate-400" data-testid="mc-link-pin-log-empty">No resets yet.</div>
            : <ul className="text-xs text-slate-600 space-y-0.5 max-h-32 overflow-y-auto">
                {[...resets].reverse().map((r) => (
                  <li key={r.at} data-testid={`mc-link-pin-log-${r.at}`}>{r.link_name} · reset {when(r.at)} by {r.by}</li>
                ))}
              </ul>}
        </div>
      </DialogContent>
    </Dialog>
  );
};
